import React, { useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; // Import the plugin for jsPDF
import IconButton from '../../elements/IconButton';
import Button from '../../elements/Button';

const Main = (props) => {
    const [loadedData, setLoadedData] = useState([]);
    const [stillGettingData, setStillGettingData] = useState(0);
    const isCancelled = useRef(false); // Used to track if fetching was cancelled

    const handleCancelFetch = () => {
        isCancelled.current = true; // Set flag to true when fetching is cancelled
    };

    const fetchAllData = (exportType) => {
        let skip = 0;
        const limit = 500;
        let allData = [];
        setStillGettingData(1);

        const fetchNext = () => {
            if (isCancelled.current) {
                console.log("Fetching has been cancelled by the user.");
                setStillGettingData(0); // Update state to reflect cancellation
                return;
            }
            getData(skip, limit, (hasMoreData, newData) => {
                if (newData !== undefined) {
                    allData = allData.concat(newData);
                    skip += limit;
                    fetchNext();
                } else {
                    if (exportType === 'CSV') {
                        downloadCSV(allData, props.exportKeys, props.fileTitle);
                    } else if (exportType === 'PDF') {
                        downloadPDF(allData, props.exportKeys, props.fileTitle);
                    }
                    setStillGettingData(2);
                }
            });
        };

        fetchNext();
    };

    const getData = (skip, limit, onCompletion) => {
        var myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + localStorage.getItem('access_token'));
        myHeaders.append("Content-Type", "application/json");

        var requestOptions = {
            method: 'POST',
            headers: myHeaders,
            redirect: 'follow',
            body: JSON.stringify(props.filterData)
        };

        props.tokenSafeAPIRequest(
            `${props.url}${props.specific ? '/' + props.specific : ''}?limit=${limit}&fetch_links=true&skip=${skip}&sort_field=${props.sort}&start_date=${props.calStartDate}&end_date=${props.calEndDate}&active_only=false`,
            requestOptions,
            '',
            (result, status) => {
                if ('postProcessing' in props) {
                    result = props.postProcessing(result);
                }

                console.log(result)

                if ('reports' in result) {
                    processResult(result.reports, onCompletion);
                } else if ('accounts' in result) {
                    processResult(result.accounts, onCompletion);
                } else if ('transactions' in result) {
                    processResult(result.transactions, onCompletion);
                } else if ('dealerships' in result) {
                    processResult(result.dealerships, onCompletion);
                } else if ('spiffs' in result) {
                    processResult(result.spiffs, onCompletion);
                } else if ('dppps_and_gap' in result) {
                    processResult(result.dppps_and_gap, onCompletion);
                } else {
                    processResult(result, onCompletion);
                }
            }
        );
    };

    const processResult = (data, onCompletion) => {
        if (data && data.length > 0) {
            setLoadedData((loadedData) => [...loadedData, ...data]);
            onCompletion(true, data);
        } else {
            onCompletion(false);
        }
    };

    function formatNumber(value) {
        try {
            // Attempt to convert the input to a number
            const num = parseFloat(value);
            // Check if the conversion resulted in a valid number
            if (!isNaN(num)) {
              // Use Intl.NumberFormat for US-style number formatting with two decimal places
              return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(num);
            } else {
              // Return a default value if the input is not a valid number
              return '0.00';
            }
          } catch (e) {
            // In case of any unexpected errors, return a default value
            return '0.00';
          }
    }

    const convertPayeeToUser = (payee, user, spiffee, dealership) => {
        if (payee === null) {return 'No User'}
        if (payee === 0) {return spiffee.name}
        if (payee === 1) {return dealership.name}
        if (payee === 2) {return user === null ? 'No Agent':(user?.first_name + ' ' + user?.last_name)}
        if (payee === 3) {return 'Inside Sales'}
    }

    const downloadCSV = (data, keys, filename = 'data.csv') => {

        let csvContent = "";
        let lastUserId = null;
        let total = 0;

        csvContent += "Agent,Inputtee or Recipient,Dealership,Account,In/Out,QTY,Amount,Total Amount,Date,Reason\r\n";

        data.map((transactions) => {
            if (transactions.user_ID?.id !== lastUserId) {
                csvContent += "\r\n";
            }
            total += transactions.amount * transactions.quantity;
            csvContent += `${transactions?.user_ID?.first_name + ' ' + transactions?.user_ID?.last_name},${convertPayeeToUser(transactions.payee, transactions?.user_ID, transactions?.spiffee_ID, transactions?.dealership_ID)},${transactions?.dealership_ID?.name},${transactions?.account_ID?.name},${transactions.input ? "In":"Out"},${transactions.quantity},${transactions.amount},${transactions.quantity * transactions.amount},${transactions.timestamp},${transactions.reason}\r\n`;
            lastUserId = transactions.user_ID?.id;
        })

        csvContent += `Total: ${total}\r\n`;
    
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };
    
    const downloadPDF = (data, keys, filename = 'data.pdf') => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.text(props.fileTitle || 'Report', 20, 10);

        const addPageNumbers = (doc) => {
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height;
            const pageWidth = pageSize.width;

            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.text(`Page ${i}`, pageWidth - 30, pageHeight - 10);
            }
        };

        // Sort by user_ID.id to ensure contiguous grouping (optional but helpful)
        data.sort((a, b) => (a.user_ID?.id > b.user_ID?.id ? 1 : -1));

        const transactionsTableData = [];
        const rowStyles = []; // Store custom style for each row
        let lastUserId = null;

        let total = 0;

        data.forEach((transaction) => {

            total += transaction.amount * transaction.quantity

            if (transaction.user_ID?.id !== lastUserId) {
                transactionsTableData.push({
                    isAgentEnd: true,
                    data: [
                        {content: '', colSpan: 7, styles: { fillColor: [255,255,255], minCellHeight: 0.1, cellPadding: 0 } }
                    ],
                })
            }

            transactionsTableData.push([
                transaction?.user_ID?.first_name + ' ' + transaction?.user_ID?.last_name,
                convertPayeeToUser(transaction.payee, transaction?.user_ID, transaction?.spiffee_ID, transaction?.dealership_ID),
                transaction?.dealership_ID?.name,
                transaction?.account_ID?.name,
                transaction.input ? 'INPUT' : 'OUTPUT',
                transaction.quantity,
                '$' + formatNumber(transaction.amount),
                '$' + formatNumber(transaction.amount * transaction.quantity),
                transaction.timestamp,
                transaction.reason,
            ]);

            lastUserId = transaction.user_ID?.id;
        });

        doc.autoTable({
            startY: 32.5,
            head: [['Agent', 'Inputtee or Recipient', 'Dealership', 'Account', 'In/Out', 'QTY', 'Amount', 'Total Amount', 'Date', 'Reason']],
            body: transactionsTableData,
            styles: {
                lineColor: [200, 200, 200],
                lineWidth: 0.1,
            },
            bodyStyles: {
                fillColor: false,
            },
            headStyles: {
                fillColor: false,
                textColor: 0,
                lineColor: [200, 200, 200],
                lineWidth: 0.1,
            },
            tableLineColor: [200, 200, 200],
            tableLineWidth: 0.1,

            // Apply per-row custom styling
            willDrawCell: function(data) {
                const rowInfo = transactionsTableData[data.row.index];
                if (rowInfo?.isAgentEnd) {
                    data.cell.styles.lineWidth = { top: 0.1, right: 0.1, bottom: 2.0, left: 0.1 };
                    data.cell.styles.lineColor = { bottom: [255,255,255] };
                }
            }
        });

        doc.text(`Total: $` + formatNumber(total), 14, doc.lastAutoTable.finalY + 30);

        addPageNumbers(doc);
        doc.save(filename);
    };
    

    return (
        <div style={{ width: '360px', height: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flexBasis: '10%', display: 'flex', flexDirection: 'row' }}>
                <span style={{ fontWeight: '500', fontSize: '20px', flexBasis: '99%' }}>Export Report</span>
                <IconButton img={false} colors={props.colors} Style={{ borderRadius: '3px', backgroundColor: props.colors.border, width: '24px' }} size="16px">
                    <div onClick={() => { handleCancelFetch(); props.setIsOpen() }} style={{ paddingLeft: '3px', paddingRight: '3px' }}><span onClick={() => { handleCancelFetch(); props.setIsOpen() }}>x</span></div>
                </IconButton>
            </div>
            <div style={{ flexBasis: '90%', display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
                <span style={{ fontWeight: '500', fontSize: '16px' }}>Downloaded Rows: {loadedData.length}</span>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('CSV')}>Download All CSV</Button>
                <Button colors={props.colors} active={true} Style={{ marginTop: '10px' }} onClick={() => fetchAllData('PDF')}>Download All PDF</Button>
            </div>
        </div>
    );
};

export default Main;
